/**
 * useRelays — relay list management, persisted to scoped localStorage.
 */

import { useState, useCallback, useEffect, useRef } from 'react';
import { scopedGet, scopedSet } from '@/lib/scoped-storage';
import { useStorageScope } from '@/hooks/useStorageScope';

const RELAYS_KEY = 'relays';

export const DEFAULT_RELAYS = [
  'wss://relay.ditto.pub',
  'wss://relay.primal.net',
];

function loadRelays(): string[] {
  try {
    const raw = scopedGet(RELAYS_KEY);
    if (raw) {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed) && parsed.length > 0) return parsed;
    }
  } catch { /* ignore */ }
  return [...DEFAULT_RELAYS];
}

function saveRelays(relays: string[]): void {
  scopedSet(RELAYS_KEY, JSON.stringify(relays));
}

export function useRelays() {
  const scope = useStorageScope();
  const scopeRef = useRef(scope);
  const [relays, setRelaysState] = useState<string[]>(loadRelays);

  // Reload when scope changes (login/logout/switch user)
  useEffect(() => {
    if (scopeRef.current !== scope) {
      scopeRef.current = scope;
      setRelaysState(loadRelays());
    }
  }, [scope]);

  const setRelays = useCallback((newRelays: string[]) => {
    const filtered = newRelays.filter((r) => r.startsWith('wss://') || r.startsWith('ws://'));
    setRelaysState(filtered);
    saveRelays(filtered);
  }, []);

  const addRelay = useCallback((url: string) => {
    setRelaysState((prev) => {
      if (prev.includes(url)) return prev;
      const next = [...prev, url];
      saveRelays(next);
      return next;
    });
  }, []);

  const removeRelay = useCallback((url: string) => {
    setRelaysState((prev) => {
      const next = prev.filter((r) => r !== url);
      saveRelays(next);
      return next;
    });
  }, []);

  return { relays, setRelays, addRelay, removeRelay };
}
